// API Usage Dashboard
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuth } from '@/providers/AuthProvider';
import {
  Code2,
  FileText,
  Key,
  ArrowRight,
  Sparkles,
  BarChart3,
  Plus,
  ExternalLink,
  Monitor,
  ArrowLeftRight,
  Webhook,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Calculator,
  Banknote,
  Building2,
  Star,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { StatCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PoweredByTao } from '@/components/ui/tao-logo';
import { toast } from '@/hooks/use-toast';

export const Route = createFileRoute('/api-home')({
  component: ApiDashboard,
});

function ApiDashboard() {
  const { user, isReady } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.templates.list(),
    enabled: isReady,
  });

  // Fetch API keys
  const { data: keysData, isLoading: keysLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.apiKeyManagement.listKeys(),
    enabled: isReady,
  });

  // Fetch usage/billing data (self-service)
  const { data: usageData } = useQuery({
    queryKey: ['my-usage'],
    queryFn: () => api.billing.myUsage(),
    enabled: isReady,
  });

  // Fetch recent webhook deliveries
  const { data: webhookData } = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () => api.webhooks.listDeliveries({ limit: 5 }),
    enabled: isReady,
  });

  const switchModeMutation = useMutation({
    mutationFn: () => api.users.update({ preferredMode: 'direct' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast({ title: 'Switched to Direct mode' });
      navigate({ to: '/dashboard' });
    },
  });

  const templates = templatesData?.data || [];
  const apiKeys = keysData?.data || [];
  const activeKeys = apiKeys.filter((k: any) => k.isActive);
  const webhookDeliveries = webhookData?.data || [];

  return (
    <div className="min-h-screen bg-background gradient-mesh noise">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl" />

        <div className="relative px-8 pt-10 pb-8">
          <div className="flex items-start justify-between mb-10">
            <div className="animate-slideUp">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <Code2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs font-medium text-emerald-400 uppercase tracking-wider">
                    API Dashboard
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date().toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <h1 className="text-4xl font-display font-bold text-foreground mb-2">
                Welcome back, <span className="text-emerald-400">{user?.name?.split(' ')[0] || 'User'}</span>
              </h1>
              <p className="text-muted-foreground text-lg">
                Manage your API integration, templates, and usage
              </p>
            </div>

            <div className="flex items-center gap-3 animate-slideUp" style={{ animationDelay: '0.1s' }}>
              {/* Mode Toggle */}
              <button
                onClick={() => switchModeMutation.mutate()}
                disabled={switchModeMutation.isPending}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gold-500/40 bg-gold-500/10 text-gold-400 hover:bg-gold-500/20 hover:border-gold-500/60 hover:text-gold-300 transition-all text-sm font-medium disabled:opacity-50 shadow-lg shadow-gold-500/5"
                title="Switch to Direct mode"
              >
                <ArrowLeftRight className="w-4 h-4" />
                <Monitor className="w-4 h-4" />
                Direct Mode
              </button>

              <Link to="/templates/new" search={{ edit: undefined }}>
                <Button variant="gold" size="lg" className="gap-2">
                  <Plus className="w-5 h-5" />
                  New Template
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            <StatCard
              title="Templates"
              value={templates.length}
              icon={<FileText className="w-5 h-5" />}
              variant="gold"
            />
            <StatCard
              title="Active API Keys"
              value={activeKeys.length}
              icon={<Key className="w-5 h-5" />}
              variant="emerald"
            />
            <StatCard
              title="Documents Processed"
              value={usageData?.summary?.documents || 0}
              icon={<BarChart3 className="w-5 h-5" />}
              variant="warning"
            />
            <StatCard
              title="API Calls"
              value={usageData?.providerCosts?.reduce((sum: number, p: any) => sum + (p.calls || 0), 0) || 0}
              icon={<Code2 className="w-5 h-5" />}
              variant="muted"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Links */}
          <div className="lg:col-span-1 animate-slideUp" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-xl font-display font-semibold text-foreground mb-6">Quick Links</h2>
            <div className="space-y-4">
              <QuickLink
                title="API Keys"
                description="Create and manage API keys"
                icon={<Key className="w-5 h-5" />}
                to="/key-management"
                isPrimary
              />
              <QuickLink
                title="New Template"
                description="Build a new analysis template"
                icon={<Plus className="w-5 h-5" />}
                to="/templates/new"
                search={{ edit: undefined }}
              />
              <QuickLink
                title="Usage & Billing"
                description="View usage and cost breakdown"
                icon={<BarChart3 className="w-5 h-5" />}
                to="/settings"
              />
              <a
                href="https://docs.creditnova.io"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-5 rounded-xl border glass border-border/50 hover:border-emerald-500/30 transition-all duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-muted text-muted-foreground group-hover:bg-emerald-500/10 group-hover:text-emerald-400 transition-all duration-300">
                    <ExternalLink className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">API Documentation</p>
                    <p className="text-sm text-muted-foreground">View full API reference</p>
                  </div>
                  <ArrowRight className="w-5 h-5 ml-auto opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0 text-muted-foreground" />
                </div>
              </a>
            </div>

            {/* Recent Webhook Deliveries */}
            {webhookDeliveries.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-4">
                  <Webhook className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-medium text-foreground">Recent Webhooks</h3>
                </div>
                <div className="space-y-2">
                  {webhookDeliveries.slice(0, 5).map((delivery: any) => (
                    <div key={delivery.id} className="glass rounded-lg p-3 flex items-center gap-3">
                      {delivery.success ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {delivery.reportId || 'Webhook delivery'}
                        </p>
                        <p className="text-2xs text-muted-foreground">
                          {delivery.statusCode} &bull; {delivery.durationMs}ms
                        </p>
                      </div>
                      <span className="text-2xs text-muted-foreground flex-shrink-0">
                        {formatDate(delivery.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Templates List */}
          <div className="lg:col-span-2 animate-slideUp" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-display font-semibold text-foreground">Your Templates</h2>
              <Link
                to="/templates"
                className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors group"
              >
                View all
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {templatesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="skeleton h-40 rounded-xl" />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <TemplateEmptyState />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.slice(0, 6).map((template: any) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            )}

            {/* API Keys Overview */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-display font-semibold text-foreground">API Keys</h2>
                <Link
                  to="/key-management"
                  className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors group"
                >
                  Manage
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {keysLoading ? (
                <div className="skeleton h-24 rounded-xl" />
              ) : apiKeys.length === 0 ? (
                <div className="glass rounded-xl p-6 text-center">
                  <Key className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">No API keys yet</p>
                  <Link to="/key-management">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Create API Key
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {apiKeys.slice(0, 3).map((key: any) => (
                    <div key={key.id} className="glass rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'w-2 h-2 rounded-full',
                          key.isActive ? 'bg-emerald-400' : 'bg-muted-foreground'
                        )} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{key.name}</p>
                          <p className="text-2xs text-muted-foreground font-mono">{key.publicKey}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className={cn(
                          'px-2 py-0.5 rounded-full text-2xs font-medium',
                          key.environment === 'production'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : 'bg-amber-500/10 text-amber-400'
                        )}>
                          {key.environment}
                        </span>
                        {key.lastUsedAt && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(key.lastUsedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-8 py-6 border-t border-border/30">
        <div className="flex items-center justify-between">
          <PoweredByTao />
          <p className="text-xs text-muted-foreground/50">
            {new Date().getFullYear()} TAO Solutions. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Quick Link card
function QuickLink({ title, description, icon, to, search, isPrimary }: {
  title: string; description: string; icon: React.ReactNode; to: string; search?: Record<string, unknown>; isPrimary?: boolean;
}) {
  return (
    <Link
      to={to}
      search={search as any}
      className={cn(
        'block p-5 rounded-xl border transition-all duration-300 group',
        isPrimary
          ? 'glass-elevated border-emerald-500/30 hover:border-emerald-500/50'
          : 'glass border-border/50 hover:border-emerald-500/30'
      )}
    >
      <div className="flex items-center gap-4">
        <div className={cn(
          'p-3 rounded-xl transition-all duration-300',
          isPrimary
            ? 'bg-emerald-500/20 text-emerald-400 group-hover:bg-emerald-500 group-hover:text-primary-950'
            : 'bg-muted text-muted-foreground group-hover:bg-emerald-500/10 group-hover:text-emerald-400'
        )}>
          {icon}
        </div>
        <div>
          <p className={cn('font-semibold', isPrimary ? 'text-emerald-400' : 'text-foreground')}>{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <ArrowRight className={cn(
          'w-5 h-5 ml-auto opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0',
          isPrimary ? 'text-emerald-400' : 'text-muted-foreground'
        )} />
      </div>
    </Link>
  );
}

// Template Card (compact version for dashboard)
function TemplateCard({ template }: { template: any }) {
  const config = template.config || {};
  const modules = config.modules || {};

  return (
    <Link
      to="/templates"
      className="glass rounded-xl p-5 hover:glass-elevated transition-all duration-300 group block"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 bg-emerald-500/10 text-emerald-500 rounded-lg">
          <FileText className="w-5 h-5" />
        </div>
        {template.isDefault && (
          <span className="flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">
            <Star className="w-3 h-3 fill-current" />
            Default
          </span>
        )}
      </div>
      <h3 className="font-semibold text-foreground group-hover:text-emerald-400 transition-colors truncate mb-1">
        {template.name}
      </h3>
      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
        {template.description || 'No description'}
      </p>
      <div className="flex gap-2">
        {modules.financial?.enabled && <Calculator className="w-3.5 h-3.5 text-blue-400/60" />}
        {modules.creditReport?.enabled && <CreditCard className="w-3.5 h-3.5 text-purple-400/60" />}
        {modules.bank?.enabled && <Banknote className="w-3.5 h-3.5 text-green-400/60" />}
        {modules.corporate?.enabled && <Building2 className="w-3.5 h-3.5 text-orange-400/60" />}
      </div>
    </Link>
  );
}

// Template empty state
function TemplateEmptyState() {
  return (
    <div className="glass-elevated rounded-2xl p-12 text-center">
      <div className="inline-flex mb-6">
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
            <FileText className="w-10 h-10 text-emerald-400" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary-950" />
          </div>
        </div>
      </div>
      <h3 className="text-xl font-display font-semibold text-foreground mb-2">No templates yet</h3>
      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
        Create your first template to define how CreditNova analyzes documents via API.
      </p>
      <Link to="/templates/new" search={{ edit: undefined }}>
        <Button variant="gold" size="lg" className="gap-2">
          <Plus className="w-5 h-5" />
          Create Template
        </Button>
      </Link>
    </div>
  );
}
